import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api.js';

/**
 * useInvoices — paginated invoice list.
 *
 * Endpoint: GET /api/dashboard/billing/invoices?limit=20&starting_after=inv_XXX
 *
 * Cursor-based pagination via Stripe's `starting_after` parameter.
 * Each invoice has:
 *   id, number, status, total_pence, currency, period_start, period_end,
 *   created_at, hosted_invoice_url, invoice_pdf
 *
 * The hosted_invoice_url is Stripe-hosted — safe to link directly.
 * The invoice_pdf is a pre-signed Stripe URL good for ~30 days.
 */
export function useInvoices() {
  const [state, setState] = useState({
    invoices: [],
    hasMore: false,
    loading: true,
    loadingMore: false,
    error: null,
    cursor: null,
  });

  const loadInitial = useCallback(async () => {
    setState(s => ({ ...s, loading: true, error: null, invoices: [], cursor: null }));
    try {
      const data = await api.get('/api/dashboard/billing/invoices?limit=20');
      setState({
        invoices: data.invoices || [],
        hasMore: !!data.has_more,
        loading: false,
        loadingMore: false,
        error: null,
        cursor: data.next_cursor || null,
      });
    } catch (e) {
      setState(s => ({ ...s, loading: false, loadingMore: false, error: e }));
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!state.cursor) return;
    setState(s => ({ ...s, loadingMore: true }));
    try {
      const data = await api.get(
        `/api/dashboard/billing/invoices?limit=20&starting_after=${encodeURIComponent(state.cursor)}`,
      );
      setState(s => ({
        invoices: [...s.invoices, ...(data.invoices || [])],
        hasMore: !!data.has_more,
        loading: false,
        loadingMore: false,
        error: null,
        cursor: data.next_cursor || null,
      }));
    } catch (e) {
      setState(s => ({ ...s, loadingMore: false, error: e }));
    }
  }, [state.cursor]);

  useEffect(() => {
    loadInitial();
  }, [loadInitial]);

  return { ...state, refresh: loadInitial, loadMore };
}
